import type {SortDescriptor} from "@project/design-system-web";
import type {PaginationState, SortingState} from "@tanstack/react-table";

import {useEffect} from "react";

import {toSortDescriptor} from "../utils/to-sort-descriptor";

interface UseTableURLStateOptions<TSearch> {
  /**
   * The navigate function from the route's `useNavigate()` hook.
   */
  navigate: (opts: {search: (prev: TSearch) => TSearch}) => unknown;
  /**
   * The current search params from the route.
   */
  search: TSearch;
  /**
   * The total number of items from the server response.
   */
  total?: number;
  /**
   * Set of column ids that are allowed to be sorted.
   */
  sortableColumns?: Set<string>;
  /**
   * Search parameter used for the active sort column. Defaults to legacy `sortBy`.
   */
  sortParam?: "sortBy" | "sortKey";
}

type BaseSearch = {
  page?: number;
  perPage?: number;
  sortBy?: string;
  sortKey?: string;
  sortDir?: string;
  q?: string;
};

/**
 * A hook that encapsulates the synchronization between TanStack Router search params
 * and TanStack Table's pagination, sorting, and global filter state.
 */
export function useTableURLState<TSearch extends BaseSearch>({
  navigate,
  search,
  sortableColumns,
  sortParam = "sortBy",
  total = 0,
}: UseTableURLStateOptions<TSearch>) {
  const page = search.page ?? 1;
  const perPage = search.perPage ?? 10;

  const totalPages = Math.max(1, Math.ceil(total / perPage));
  const visiblePage = Math.min(page, totalPages);

  const pagination: PaginationState = {pageIndex: visiblePage - 1, pageSize: perPage};

  const sortColumn = search[sortParam];
  const sorting: SortingState = sortColumn
    ? [{desc: search.sortDir === "desc", id: sortColumn}]
    : [];

  const sortDescriptor = toSortDescriptor(sorting);

  // Auto-correction effect: if the URL requests a page that no longer exists
  // (e.g. after deleting the last item on that page), safely redirect to the last valid page.
  useEffect(() => {
    // Only auto-correct if we actually have data loaded and the requested page is out of bounds
    if (total > 0 && page > totalPages) {
      void navigate({search: (prev: TSearch) => ({...prev, page: totalPages}) as TSearch});
    }
  }, [page, totalPages, total, navigate]);

  function onPageChange(targetPage: number) {
    const nextPage = Math.min(Math.max(targetPage, 1), totalPages);

    if (nextPage === page) return;

    void navigate({search: (prev: TSearch) => ({...prev, page: nextPage}) as TSearch});
  }

  function onPageSizeChange(newSize: number) {
    void navigate({search: (prev: TSearch) => ({...prev, perPage: newSize, page: 1}) as TSearch});
  }

  function onSortChange(descriptor: SortDescriptor) {
    const column = descriptor.column ? String(descriptor.column) : undefined;

    if (column && sortableColumns && !sortableColumns.has(column)) return;

    void navigate({
      search: (prev: TSearch) =>
        ({
          ...prev,
          page: 1,
          [sortParam]: column,
          sortDir: descriptor.direction === "descending" ? "desc" : "asc",
        }) as TSearch,
    });
  }

  function onSearchChange(q?: string) {
    void navigate({search: (prev: TSearch) => ({...prev, page: 1, q: q || undefined}) as TSearch});
  }

  return {
    globalFilter: search.q as string | undefined,
    onPageChange,
    onPageSizeChange,
    onSearchChange,
    onSortChange,
    pagination,
    sortDescriptor,
    sorting,
    totalPages,
    visiblePage,
  };
}
